import { z } from "zod";

import { CalcDecimal, decimalString, moneyString } from "./decimal.js";
import {
  MONEY_ROUNDING_MODE,
  QUOTE_FINANCIALS_ROUNDING_POLICY_ID,
  roundedMoneyDecimal,
} from "./rounding-policy.js";

export const QUOTE_FINANCIALS_VERSION = "1.1.0";
export const PUBLIC_QUOTE_FINANCIALS_VERSION = "1.0.0";

const plainDecimalSchema = z.string().regex(
  /^(?:0|[1-9]\d{0,21})(?:\.\d{1,6})?$/,
  "Value must be a plain decimal string",
);

const positiveMoneySchema = z.string().regex(
  /^(?:0|[1-9]\d{0,21})(?:\.\d{1,2})?$/,
  "Amount must be a plain decimal string with at most two decimal places",
).refine((value) => new CalcDecimal(value).gt(0), "Amount must be greater than zero");

const percentageSchema = plainDecimalSchema.refine(
  (value) => new CalcDecimal(value).lte(100),
  "Percentage must not exceed 100",
);

const additionalItemSchema = z.object({
  itemId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
  label: z.string().trim().min(1).max(200),
  amount: positiveMoneySchema,
}).strict();

export const quoteFinancialsInputSchema = z.object({
  verifiedCompensation: positiveMoneySchema,
  discountPercentage: percentageSchema,
  expensesPercentage: percentageSchema,
  socialSecurityPercentage: percentageSchema,
  vatPercentage: percentageSchema,
  additionalItems: z.array(additionalItemSchema).max(50),
}).strict().superRefine((input, context) => {
  const ids = new Set<string>();
  input.additionalItems.forEach((item, index) => {
    if (ids.has(item.itemId)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate additional item ID: ${item.itemId}`,
        path: ["additionalItems", index, "itemId"],
      });
    }
    ids.add(item.itemId);
  });
});

export type QuoteFinancialsInput = z.infer<typeof quoteFinancialsInputSchema>;

export interface QuoteFinancialsResult {
  readonly calculationVersion: string;
  readonly roundingPolicy: "round_each_stage_to_cent";
  readonly moneyRoundingMode: typeof MONEY_ROUNDING_MODE;
  readonly roundingPolicyId: typeof QUOTE_FINANCIALS_ROUNDING_POLICY_ID;
  readonly normalizedInput: QuoteFinancialsInput;
  readonly totals: {
    readonly verifiedCompensation: string;
    readonly discount: string;
    readonly compensationAfterDiscount: string;
    readonly additionalItems: string;
    readonly professionalSubtotal: string;
    readonly expenses: string;
    readonly socialSecurityBase: string;
    readonly socialSecurityContribution: string;
    readonly vatBase: string;
    readonly vat: string;
    readonly grandTotal: string;
  };
}

export class QuoteFinancialsInputError extends Error {
  readonly issues: z.core.$ZodIssue[];

  constructor(issues: z.core.$ZodIssue[]) {
    super("Quote financial input is invalid");
    this.name = "QuoteFinancialsInputError";
    this.issues = issues;
  }
}

function cents(value: InstanceType<typeof CalcDecimal>): InstanceType<typeof CalcDecimal> {
  return roundedMoneyDecimal(value);
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

export function calculateQuoteFinancials(rawInput: unknown): QuoteFinancialsResult {
  const parsed = quoteFinancialsInputSchema.safeParse(rawInput);
  if (!parsed.success) throw new QuoteFinancialsInputError(parsed.error.issues);

  const normalizedInput: QuoteFinancialsInput = {
    verifiedCompensation: moneyString(new CalcDecimal(parsed.data.verifiedCompensation)),
    discountPercentage: decimalString(new CalcDecimal(parsed.data.discountPercentage)),
    expensesPercentage: decimalString(new CalcDecimal(parsed.data.expensesPercentage)),
    socialSecurityPercentage: decimalString(
      new CalcDecimal(parsed.data.socialSecurityPercentage),
    ),
    vatPercentage: decimalString(new CalcDecimal(parsed.data.vatPercentage)),
    additionalItems: parsed.data.additionalItems.map((item) => ({
      itemId: item.itemId,
      label: item.label.trim(),
      amount: moneyString(new CalcDecimal(item.amount)),
    })),
  };

  const verifiedCompensation = new CalcDecimal(normalizedInput.verifiedCompensation);
  const discount = cents(
    verifiedCompensation.times(normalizedInput.discountPercentage).div(100),
  );
  const compensationAfterDiscount = verifiedCompensation.minus(discount);
  const additionalItems = normalizedInput.additionalItems.reduce(
    (total, item) => total.plus(item.amount),
    new CalcDecimal(0),
  );
  const professionalSubtotal = compensationAfterDiscount.plus(additionalItems);
  const expenses = cents(
    professionalSubtotal.times(normalizedInput.expensesPercentage).div(100),
  );
  const socialSecurityBase = professionalSubtotal.plus(expenses);
  const socialSecurityContribution = cents(
    socialSecurityBase.times(normalizedInput.socialSecurityPercentage).div(100),
  );
  const vatBase = socialSecurityBase.plus(socialSecurityContribution);
  const vat = cents(vatBase.times(normalizedInput.vatPercentage).div(100));
  const grandTotal = vatBase.plus(vat);

  return freezeDeep({
    calculationVersion: QUOTE_FINANCIALS_VERSION,
    roundingPolicy: "round_each_stage_to_cent",
    moneyRoundingMode: MONEY_ROUNDING_MODE,
    roundingPolicyId: QUOTE_FINANCIALS_ROUNDING_POLICY_ID,
    normalizedInput,
    totals: {
      verifiedCompensation: moneyString(verifiedCompensation),
      discount: moneyString(discount),
      compensationAfterDiscount: moneyString(compensationAfterDiscount),
      additionalItems: moneyString(additionalItems),
      professionalSubtotal: moneyString(professionalSubtotal),
      expenses: moneyString(expenses),
      socialSecurityBase: moneyString(socialSecurityBase),
      socialSecurityContribution: moneyString(socialSecurityContribution),
      vatBase: moneyString(vatBase),
      vat: moneyString(vat),
      grandTotal: moneyString(grandTotal),
    },
  });
}

export const publicQuoteFinancialsInputSchema = z.object({
  verifiedCompensation: positiveMoneySchema,
  worksValueForExpenseLimit: positiveMoneySchema,
  procedure: z.enum([
    "article_108_2_b_best_value",
    "article_50_1_b_direct_award",
  ]),
  bimMandatory: z.boolean(),
  expensePercentage: percentageSchema,
  expenseMaximumPercentage: percentageSchema,
  reductionPercentage: percentageSchema,
  socialSecurityPercentage: percentageSchema,
  vatPercentage: percentageSchema,
  additionalItems: z.array(additionalItemSchema).max(50),
}).strict().superRefine((input, context) => {
  if (new CalcDecimal(input.expensePercentage).gt(input.expenseMaximumPercentage)) {
    context.addIssue({
      code: "custom",
      message: `Expense percentage exceeds the authoritative maximum ${input.expenseMaximumPercentage}`,
      path: ["expensePercentage"],
    });
  }
  if (
    input.procedure === "article_50_1_b_direct_award"
    && new CalcDecimal(input.reductionPercentage).gt(20)
  ) {
    context.addIssue({
      code: "custom",
      message: "Direct-award reduction must not exceed 20 percent",
      path: ["reductionPercentage"],
    });
  }
  const ids = new Set<string>();
  input.additionalItems.forEach((item, index) => {
    if (ids.has(item.itemId)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate additional item ID: ${item.itemId}`,
        path: ["additionalItems", index, "itemId"],
      });
    }
    ids.add(item.itemId);
  });
});

export type PublicQuoteFinancialsInput = z.infer<typeof publicQuoteFinancialsInputSchema>;

export interface PublicQuoteFinancialsResult {
  readonly calculationVersion: typeof PUBLIC_QUOTE_FINANCIALS_VERSION;
  readonly roundingPolicy: "round_each_stage_to_cent";
  readonly moneyRoundingMode: typeof MONEY_ROUNDING_MODE;
  readonly roundingPolicyId: typeof QUOTE_FINANCIALS_ROUNDING_POLICY_ID;
  readonly normalizedInput: PublicQuoteFinancialsInput;
  readonly totals: {
    readonly cp: string;
    readonly bimIncrement: string;
    readonly compensationAfterBim: string;
    readonly expenses: string;
    readonly additionalItems: string;
    /** Statutory Art. 41 base: CP + BIM + expenses; excludes free-form items. */
    readonly normativeBaseBeforeReduction: string;
    readonly professionalAmountBeforeReduction: string;
    readonly fixedShare65: string;
    readonly discountableShare35: string;
    readonly reduction: string;
    readonly professionalAmountAfterReduction: string;
    readonly socialSecurityContribution: string;
    readonly vatBase: string;
    readonly vat: string;
    readonly grandTotal: string;
  };
}

export function calculatePublicQuoteFinancials(rawInput: unknown): PublicQuoteFinancialsResult {
  const parsed = publicQuoteFinancialsInputSchema.safeParse(rawInput);
  if (!parsed.success) throw new QuoteFinancialsInputError(parsed.error.issues);
  const normalizedInput: PublicQuoteFinancialsInput = {
    verifiedCompensation: moneyString(new CalcDecimal(parsed.data.verifiedCompensation)),
    worksValueForExpenseLimit: moneyString(new CalcDecimal(parsed.data.worksValueForExpenseLimit)),
    procedure: parsed.data.procedure,
    bimMandatory: parsed.data.bimMandatory,
    expensePercentage: decimalString(new CalcDecimal(parsed.data.expensePercentage)),
    expenseMaximumPercentage: decimalString(new CalcDecimal(parsed.data.expenseMaximumPercentage)),
    reductionPercentage: decimalString(new CalcDecimal(parsed.data.reductionPercentage)),
    socialSecurityPercentage: decimalString(new CalcDecimal(parsed.data.socialSecurityPercentage)),
    vatPercentage: decimalString(new CalcDecimal(parsed.data.vatPercentage)),
    additionalItems: parsed.data.additionalItems.map((item) => ({
      itemId: item.itemId,
      label: item.label.trim(),
      amount: moneyString(new CalcDecimal(item.amount)),
    })),
  };

  const cp = new CalcDecimal(normalizedInput.verifiedCompensation);
  const bimIncrement = normalizedInput.bimMandatory
    ? cents(cp.times(10).div(100))
    : new CalcDecimal(0);
  const compensationAfterBim = cp.plus(bimIncrement);
  const expenses = cents(
    compensationAfterBim.times(normalizedInput.expensePercentage).div(100),
  );
  const additionalItems = normalizedInput.additionalItems.reduce(
    (total, item) => total.plus(item.amount),
    new CalcDecimal(0),
  );
  const professionalAmountBeforeReduction = compensationAfterBim.plus(expenses);
  const article108 = normalizedInput.procedure === "article_108_2_b_best_value";
  const fixedShare65 = article108
    ? cents(professionalAmountBeforeReduction.times(65).div(100))
    : new CalcDecimal(0);
  const discountableShare35 = article108
    ? professionalAmountBeforeReduction.minus(fixedShare65)
    : professionalAmountBeforeReduction;
  const reduction = cents(
    discountableShare35.times(normalizedInput.reductionPercentage).div(100),
  );
  const professionalAmountAfterReduction = professionalAmountBeforeReduction
    .minus(reduction)
    .plus(additionalItems);
  const socialSecurityContribution = cents(
    professionalAmountAfterReduction.times(normalizedInput.socialSecurityPercentage).div(100),
  );
  const vatBase = professionalAmountAfterReduction.plus(socialSecurityContribution);
  const vat = cents(vatBase.times(normalizedInput.vatPercentage).div(100));
  const grandTotal = vatBase.plus(vat);

  return freezeDeep({
    calculationVersion: PUBLIC_QUOTE_FINANCIALS_VERSION,
    roundingPolicy: "round_each_stage_to_cent",
    moneyRoundingMode: MONEY_ROUNDING_MODE,
    roundingPolicyId: QUOTE_FINANCIALS_ROUNDING_POLICY_ID,
    normalizedInput,
    totals: {
      cp: moneyString(cp),
      bimIncrement: moneyString(bimIncrement),
      compensationAfterBim: moneyString(compensationAfterBim),
      expenses: moneyString(expenses),
      additionalItems: moneyString(additionalItems),
      normativeBaseBeforeReduction: moneyString(professionalAmountBeforeReduction),
      professionalAmountBeforeReduction: moneyString(professionalAmountBeforeReduction),
      fixedShare65: moneyString(fixedShare65),
      discountableShare35: moneyString(discountableShare35),
      reduction: moneyString(reduction),
      professionalAmountAfterReduction: moneyString(professionalAmountAfterReduction),
      socialSecurityContribution: moneyString(socialSecurityContribution),
      vatBase: moneyString(vatBase),
      vat: moneyString(vat),
      grandTotal: moneyString(grandTotal),
    },
  });
}
